/* eslint-disable max-len */
/* eslint-disable no-param-reassign */
import React, { useContext, useEffect, useState } from 'react';

import PropTypes from 'prop-types';
import { ViewModelServiceContext, ViewModelForm, useDataRefresh } from '@xengage/gw-portals-viewmodel-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { useTranslator } from '@jutro/locale';
import { translateContent } from 'ml-components-platform-react';
import '@xengage/gw-platform-translations';
import _ from 'lodash';
import moment from 'moment';

// eslint-disable-next-line import/no-unresolved
import appConfig from 'app-config';
import { MLProductCodeUtil } from 'ml-portals-util-js';
import { MLGafUtil, MLBillingUtil, MLNotificationBlockUtil } from 'ml-portals-util-react';
import { MLNavigationStepsTrackingService } from 'ml-services-common-react';
import Address from '../Models/Address';
import MLInsuredCandidatesComponent from '../MLInsuredCandidatesComponent/MLInsuredCandidatesComponent';
import MLNameDetailsComponent from '../MLNameDetailsComponent/MLNameDetailsComponent';
import MLAddressDetailsComponent from '../MLAddressDetailsComponent/MLAddressDetailsComponent';
import MLPayrollDeductComponent from './MLPayrollDeductComponent';
import MLPaymentEmployeeDetailsComponent from '../MLPaymentEmployeeDetailsComponent/MLPaymentEmployeeDetailsComponent';
import messages from './MLPaymentMethodComponent.messages';
import metadata from './MLPaymentMethodComponent.metadata.json5';
import styles from './MLPaymentMethodComponent.module.scss';

import { IntlContext } from '@jutro/legacy/locale';

// ML: MLPaymentDetailsMethodDirectiveCtrl.js, ml-payment-details-method.html

const {
    MLAccountPaymentMethods,
    MLRatingPaymentMethods,
    MLUserTypes,
    MLProducerTypes,
    MLMinDaysToUseBankOpt,
    mercerGpcCode
} = appConfig;

const {
    MLMortgageePayPlanPublicId,
    MLPayrollDeductPlanPublicId
} = appConfig.mlPaymentPlan;

function MLPaymentMethodComponent(props) {
    const gafPathValue = 'baseData.gafInfo_ML.value';
    const translator = useTranslator();
    const { refreshData } = useDataRefresh();

    const {
        id,
        onValidate,
        setShowErrors,
        showValidationErrors,
        submissionVM,
        paymentDetails,
        paymentInstruments,
        gafInfoObject,
        hasSsn,
        hasDepartmentNumber,
        hasEmployeeNumber,
        hasMetroBadgeNumber,
        gpaMode,
        qnbMode,
        mortgageeDetails,
        periodStartDate,
        productCode,
        coveragePartType,
        premium,
        paymentPlans,
        hasMortgagees,
        isEndorsement,
        isCitrixSecureEnv,
        currentUser,
        onRatingPaymentMethodChange,
        selectPaymentPlan,
        refreshPaymentPage,
        steps,
        jumpTo,
        addAndJumpToInterestedPartiesStep,
        updateShowErrors,
        updateWizardData,
        isBasedOnPayroll,
    } = props;

    const gpcCode = _.get(submissionVM,`${gafPathValue}.gpcCode`, undefined)

    const viewModelService = useContext(ViewModelServiceContext);
    const { authHeader } = useAuthentication();

    const { onValidate: setComponentValidation, isComponentValid, disregardFieldValidation } = useValidation(id);
    const gafPathMl =_.get(submissionVM, 'baseData.gafInfo_ML');

    useEffect(() => {
        if (onValidate) {
            onValidate(isComponentValid, id);
        }
    }, [id, onValidate, isComponentValid]);

    const intl = useContext(IntlContext);
    const formatAmountString = (amount) => {
        try {
            return intl.formatNumber(amount.amount, { style: 'currency', currency: amount.currency });
        } catch (e) {
            return '';
        }
    };

    const {
        getPaymentMethodName
    } = MLBillingUtil();

    const {
        validatePayrollDeductEligibility,
        ELIG_STATUS
    } = MLGafUtil();

    const AlertService = MLNotificationBlockUtil();

    const [otherPayorSelected, setOtherPayorSelected] = useState(_.get(paymentDetails, 'otherPayor_ML.publicID.value') !== undefined);

    useEffect(() => {
        if (_.get(paymentDetails, 'otherPayor_ML.publicID.value') !== undefined) {
            _.set(paymentDetails, 'otherPayor_ML.updated.value', true);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const paymentMethodTypeList = viewModelService.productMetadata.get('pc').types.getTypelist('RatingPaymentMethod_ML');
    const emptyAddress = viewModelService.create(new Address(), 'pc', 'edge.capabilities.address.dto.AddressDTO');

    const getRatingPaymentMethodCode = () => {
        return paymentDetails.ratingPaymentMethod_ML.value ? paymentDetails.ratingPaymentMethod_ML.value.code : '';
    };

    const isPayrollDeductOptionAvailable = () => {
        const isPayrollEligible = validatePayrollDeductEligibility(gafInfoObject.gafInfo, submissionVM, qnbMode);
        return gafInfoObject.choseEligibility && isPayrollEligible;
    };

    const isPayrollDeductionSelected = () => {
        return getRatingPaymentMethodCode() === MLRatingPaymentMethods.PAYROLL_DEDUCT;
    };

    const isPayOnlineOrMailSelected = () => {
        return getRatingPaymentMethodCode() === MLRatingPaymentMethods.PAY_ONLINE_OR_BY_MAIL;
    };

    const isBankOptAvailable = () => {
        if (isEndorsement || gpaMode) {
            return true;
        }
        const daysToEffectiveDate = moment().startOf('day').diff(periodStartDate, 'days');
        return daysToEffectiveDate < MLMinDaysToUseBankOpt;
    };

    const isMortgageOptAvailable = () => {
        return MLProductCodeUtil.isHomeOwners(productCode.value) && !MLProductCodeUtil.isHomeOwnersPartialTypeRental(coveragePartType);
    };

    const payrollDeductOption = paymentDetails.ratingPaymentMethod_ML.aspects.availableValues.find((value) => {
        return value.code === MLRatingPaymentMethods.PAYROLL_DEDUCT;
    });

    const installmentAmount = paymentPlans?.find(payment => payment.billingId === MLPayrollDeductPlanPublicId)?.installment

    const bankAccountOption = paymentDetails.ratingPaymentMethod_ML.aspects.availableValues.find((value) => {
        return value.code === MLRatingPaymentMethods.BANK_ACCOUNT;
    });

    const creditDebitCardOption = paymentDetails.ratingPaymentMethod_ML.aspects.availableValues.find((value) => {
        return value.code === MLRatingPaymentMethods.CREDIT_CARD;
    });

    const mortgageOption = paymentDetails.ratingPaymentMethod_ML.aspects.availableValues.find((value) => {
        return value.code === MLRatingPaymentMethods.MORTGAGEE;
    });

    const payOnlineMailOption = paymentDetails.ratingPaymentMethod_ML.aspects.availableValues.find((value) => {
        return value.code === MLRatingPaymentMethods.PAY_ONLINE_OR_BY_MAIL;
    });

    const getValidPaymentMethods = () => {
        const methods = [];
        if (isPayrollDeductOptionAvailable()) {
            methods.push({
                code: payrollDeductOption.code,
                name: getPaymentMethodName(payrollDeductOption.code)
            });
        }
        if (isBankOptAvailable()) {
            methods.push({
                code: bankAccountOption.code,
                name: getPaymentMethodName(bankAccountOption.code)
            });
        }
        methods.push({
            code: creditDebitCardOption.code,
            name: getPaymentMethodName(creditDebitCardOption.code)
        });
        if (isMortgageOptAvailable()) {
            methods.push({
                code: mortgageOption.code,
                name: getPaymentMethodName(mortgageOption.code)
            });
        }   
        methods.push({
            code: payOnlineMailOption.code,
            name: getPaymentMethodName(payOnlineMailOption.code)
        });
        return methods;
    };

    const goToAdditionalInfo = () => {
        if (MLNavigationStepsTrackingService.getData()) {
            MLNavigationStepsTrackingService.completeCurrentStep(null, authHeader);
        }
        const stepToJump = _.findIndex(steps, (step) => step.mlStepName === 'hoAddtlInfoToBuy');
        if (stepToJump > -1) {
            jumpTo(stepToJump)
        }
    }

    const addMortgagePaymentMethodAlert = () => {
        if (paymentDetails.ratingPaymentMethod_ML.value.code === MLRatingPaymentMethods.MORTGAGEE && !hasMortgagees) {
            if (qnbMode && !isEndorsement) {
                AlertService.addErrorAlert(messages.missingMortgageQB, 'mortgageBeingAdded', goToAdditionalInfo)
            } else if (!isEndorsement) {
                AlertService.addErrorAlert(messages.missingMortgage, 'mortgageBeingAdded', goToAdditionalInfo)
            } else {
                AlertService.addErrorAlert(messages.endorsementMissingMortgage, 'mortgageBeingAdded', goToInterestedParties)
            }
        }
    }

    const _checkForMissingMortgagee = () => {
        AlertService.clearBlockAlerts('mortgageBeingAdded')
        if (paymentDetails.ratingPaymentMethod_ML.value && paymentDetails.ratingPaymentMethod_ML.value.code === MLRatingPaymentMethods.MORTGAGEE
            && !hasMortgagees) {
            addMortgagePaymentMethodAlert();
        }
    }

    const onPaymentMethodOptionChange = (value) => {

        if (onValidate) {
            onValidate(true, id);
        }

        if(setShowErrors) {
            setShowErrors(false);
        }

        if(updateShowErrors) {
            updateShowErrors(false)
        }

        paymentDetails.ratingPaymentMethod_ML.value = paymentMethodTypeList.getCode(value.code);
        if (!isPayOnlineOrMailSelected()) {
            paymentDetails.isOtherPayorSelected_ML.value = false;
            paymentDetails.otherPayor_ML.value = undefined;
            disregardFieldValidation('nameDetailsComponent');
            disregardFieldValidation('addressDetailsComponent');
        }
        
        if(!isPayrollDeductionSelected()) {
            disregardFieldValidation('payrollDeductComponent');

            if(!_.isEmpty(gafPathMl?.gpcCode?.value)){
                _.set(submissionVM, 'baseData.gafInfo_ML.isEditPayrollDeduct.value', false);
            }
        }
        
        onRatingPaymentMethodChange(value);
        _checkForMissingMortgagee();

        if(paymentDetails?.paymentMethod?.value == "responsive") {
            paymentDetails.paymentDayOfMonth_ML.value = periodStartDate?.day; 
        }            

    };

    const onFirstYearChange = (value) => {
        paymentDetails.firstYear_ML.value = value;
        selectPaymentPlan(MLMortgageePayPlanPublicId);

        // Update validation to true if first year or second year is selected to allow user to proceed and view mortgagee details
        if (paymentDetails.ratingPaymentMethod_ML.value.code === MLRatingPaymentMethods.MORTGAGEE && hasMortgagees && !isEndorsement &&
            onValidate && !_.isNull(value) && !_.isUndefined(value)) {
            onValidate(true, id);
        }

        if(updateShowErrors) {
            updateShowErrors(false)
        }
        if(qnbMode){
            updateWizardData(submissionVM)
        } 
        refreshPaymentPage();
    };

    // other payor
    const onOtherPayorSelectedChange = (value) => {
        if (value === true) {
            // createPayorData
            paymentDetails.otherPayor_ML = {
                address: emptyAddress.value
            };
        } else {
            // clearPayorData
            paymentDetails.otherPayor_ML.value = undefined;
            disregardFieldValidation('nameDetailsComponent');
            disregardFieldValidation('addressDetailsComponent');
            setOtherPayorSelected(false);
        }
        paymentDetails.isOtherPayorSelected_ML.value = value;
        refreshData();
    };

    const updatePersonVM = () => {
        refreshData();
    };

    const updateAddressVM = () => {
        refreshData();
    };

    const goToInterestedParties = () => {
        if (MLNavigationStepsTrackingService.getData()) {
            MLNavigationStepsTrackingService.completeCurrentStep(null, authHeader);
        }
        const stepToJump = _.findIndex(steps, (step) => step.mlStepName === 'endorsementInterestedParties');
        if (stepToJump > -1) {
            jumpTo(stepToJump)
        } else {
            addAndJumpToInterestedPartiesStep && addAndJumpToInterestedPartiesStep();
        }
    }

    useEffect(() => {
        _checkForMissingMortgagee();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    // eslint-disable-next-line no-underscore-dangle
    const _hasViewablePayrollField = () => {
        const pdAvailable = isPayrollDeductOptionAvailable();
        return !hasSsn
            || (gafInfoObject.gafInfo.departmentNumberUsage.value && (!hasDepartmentNumber || pdAvailable))
            || (gafInfoObject.gafInfo.employeeNumberUsage.value && (!hasEmployeeNumber || pdAvailable))
            || (gafInfoObject.gafInfo.metroBadgeNumberUsage.value && (!hasMetroBadgeNumber || pdAvailable));
    };

    const showPayrollDeductEmployeeFields = () => {
        return !!gafInfoObject.gafInfo.payrollDeductAvailable.value
            && isPayrollDeductionSelected()
            && _hasViewablePayrollField();
    };

    // for validation
    gafInfoObject.gafInfo.isEditPayrollDeduct.value = showPayrollDeductEmployeeFields();

    const showInstallmentAmount = () => {
        return !isEndorsement && isPayrollDeductionSelected() && isPayrollDeductOptionAvailable();
    };

    const resolvers = {
        resolveComponentMap: {
            mlInsuredCandidatesComponent: MLInsuredCandidatesComponent,
            mlnamedetailscomponent: MLNameDetailsComponent,
            mladdressdetailscomponent: MLAddressDetailsComponent,
            mlpayrolldeductcomponent: MLPayrollDeductComponent,
            mlpaymentemployeedetailscomponent: MLPaymentEmployeeDetailsComponent
        },
        resolveClassNameMap: styles,
        resolveCallbackMap: {
        }
    };

    const overrides = {
        paymentMethodOptions: {
            availableValues: getValidPaymentMethods(),
            onValueChange: onPaymentMethodOptionChange,
            tooltip: {
                placement: "bottom",
                
                renderContent: () => translateContent({ 
                    tooltipContent: messages.PaymentMethodTooltipContent, 
                    tooltipHeader: messages.PaymentOptionHelpText,
                    translator
                })
            },
            className: qnbMode ? "radioButtonQnB" : ""
        },
        paymentMethodTooltipContainer: {
            visible: true
        },
        // other payor
        otherPayorContainer: {
            visible: paymentDetails.ratingPaymentMethod_ML.value.code === MLRatingPaymentMethods.PAY_ONLINE_OR_BY_MAIL
        },
        otherPayorCheckbox: {
            onValueChange: onOtherPayorSelectedChange
        },
        otherPayorInfoRow: {
            visible: paymentDetails.isOtherPayorSelected_ML.value === true
        },
        insuredCandidatesComponent: {
            submissionVM,
            otherPayorVM: paymentDetails.otherPayor_ML,
            setInsuredCandidateSelected: setOtherPayorSelected,
            initPublicID: _.get(paymentDetails, 'otherPayor_ML.publicID.value'),
            label: {
                id: 'common.views.payment-method.Add Other Payor',
                defaultMessage: 'Add Other Payor'
            },
            onValueChange: refreshData,
            viewModelService,
            showValidationErrors
        },
        formFieldsContainer: {
            visible: otherPayorSelected
        },
        nameDetailsComponent: {
            personVM: paymentDetails.otherPayor_ML,
            onModelChange: updatePersonVM,
            layout: 'ML-Name-Default',
            onValidate: setComponentValidation,
            showValidationErrors
        },
        addressDetailsComponent: {
            addressVM: paymentDetails.otherPayor_ML.address,
            onModelChange: updateAddressVM,
            layout: 'ml-policy-change',
            onValidate: setComponentValidation,
            showValidationErrors
        },
        // payroll deduct fields
        payrollDeductInfo: {
            visible: showPayrollDeductEmployeeFields(),
        },
        payrollDeductComponent: {
            id:"payrollDeductComponent",
            gafInfoObject,
            hasSsn,
            hasDepartmentNumber,
            hasEmployeeNumber,
            hasMetroBadgeNumber,
            onValidate: setComponentValidation,
            showValidationErrors,
            submissionVM,
            updateWizardData
        },
        //employee details
        paymentEmployeeDetails: {
            visible: gpaMode ? ((isPayrollDeductionSelected() && gafInfoObject.gafInfo?.eligibleForPayrollDeduct.value === ELIG_STATUS.ELIGIBLE_EFILE_VER_REQUIRED && gafInfoObject.gafInfo?.eligibilityVerified.value === true)
                    ||(!isPayrollDeductionSelected() && gafInfoObject.gafInfo?.eligibleForDirectBill.value === ELIG_STATUS.ELIGIBLE_EFILE_VER_REQUIRED)
                    ||(isPayrollDeductionSelected() && gafInfoObject.gafInfo?.eligibleForPayrollDeduct.value === ELIG_STATUS.ELIGIBLE_NO_EFILE_VER_REQUIRED)) : false
        },
        paymentemployeedetailscomponent: {
            gafInfoObject,
            onValidate: setComponentValidation,
            showValidationErrors,
            submissionVM,
            isEndorsement,
            isPayrollDeductionSelected,
            updateWizardData
        },
        // mortgatee
        mortgageeInfoRow: {
            visible: paymentDetails.ratingPaymentMethod_ML.value.code === MLRatingPaymentMethods.MORTGAGEE && hasMortgagees && !isEndorsement
        },
        firstYear: {
            value: _.toString(paymentDetails.firstYear_ML.value),
            onValueChange: onFirstYearChange,
            tooltip: {
                placement: "bottom",
                
                renderContent: () => translateContent({ 
                    tooltipContent: messages.MortgageOptionTooltipContent, 
                    tooltipHeader: messages.MortgageOptionTooltipLabel,
                    translator
                })
            }
        },
        transferOffMercer:{
            value: _.toString(paymentDetails?.sendTransferOffToMercer?.value),
            onValueChange: (value) => {
                paymentDetails.sendTransferOffToMercer.value = value
                refreshPaymentPage()
            },
            visible: isEndorsement 
            && (isBasedOnPayroll && !isPayrollDeductionSelected()) 
            && gpcCode === mercerGpcCode,
        },
        totalPremiumRow: {
            visible: gpaMode
        },
        totalPremium: {
            content: translator(messages.TotalPremium, { premium: formatAmountString(premium) })
        },
        installmentAmount:  {
            content: translator(messages.InstallmentAmount, { installmentAmount: formatAmountString(installmentAmount) }),
            visible: showInstallmentAmount()
        }
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            overrideProps={overrides}
            model={paymentDetails}
            onValidationChange={setComponentValidation}
            showErrors={showValidationErrors}
            // onModelChange={}
            componentMap={resolvers.resolveComponentMap}
            callbackMap={resolvers.resolveCallbackMap}
            classNameMap={resolvers.resolveClassNameMap}
        />
    );
}

MLPaymentMethodComponent.propTypes = {
    id: PropTypes.string.isRequired,
    onValidate: PropTypes.func.isRequired,
    showValidationErrors: PropTypes.bool.isRequired,
    paymentInstruments: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    paymentDetails: PropTypes.shape({
        isOtherPayorSelected_ML: PropTypes.shape({
            value: PropTypes.bool
        }),
        otherPayor_ML: PropTypes.shape({
            value: PropTypes.shape({}),
            address: PropTypes.shape({})
        }),
        ratingPaymentMethod_ML: PropTypes.shape({
            aspects: PropTypes.shape({
                availableValues: PropTypes.arrayOf(PropTypes.shape({}))
            }),
            value: PropTypes.shape({
                code: PropTypes.string
            })
        }),
        firstYear_ML: PropTypes.shape({
            value: PropTypes.bool
        })
    }).isRequired,
    gafInfoObject: PropTypes.shape({
        gafInfo: PropTypes.shape({
            payrollDeductAvailable: PropTypes.shape({
                value: PropTypes.bool
            }),
            ssn: PropTypes.shape({
                value: PropTypes.string
            }),
            departmentNumber: PropTypes.shape({
                value: PropTypes.string
            }),
            departmentNumberLiteral: PropTypes.shape({
                value: PropTypes.string
            }),
            employeeNumber: PropTypes.shape({
                value: PropTypes.string
            }),
            employeeNumberLiteral: PropTypes.shape({
                value: PropTypes.string
            }),
            metroBadgeNumber: PropTypes.shape({
                value: PropTypes.string
            }),
            metroBadgeNumberLiteral: PropTypes.shape({
                value: PropTypes.string
            }),
            eligibilityVerified: PropTypes.shape({
                value: PropTypes.bool
            }),
            departmentNumberUsage: PropTypes.shape({
                value: PropTypes.bool
            }),
            employeeNumberUsage: PropTypes.shape({
                value: PropTypes.bool
            }),
            metroBadgeNumberUsage: PropTypes.shape({
                value: PropTypes.bool
            }),
            isEditPayrollDeduct: PropTypes.shape({
                value: PropTypes.bool
            })
        }),
        choseEligibility: PropTypes.bool
    }).isRequired,
    hasSsn: PropTypes.bool,
    hasDepartmentNumber: PropTypes.bool,
    hasEmployeeNumber: PropTypes.bool,
    hasMetroBadgeNumber: PropTypes.bool,
    periodStartDate: PropTypes.shape({}).isRequired,
    productCode: PropTypes.shape({
        value: PropTypes.string
    }).isRequired,
    hasMortgagees: PropTypes.bool,
    isEndorsement: PropTypes.bool,
    isCitrixSecureEnv: PropTypes.bool,
    currentUser: PropTypes.shape({
        userType: PropTypes.string,
        primaryProducerCode_ML: PropTypes.shape({
            producerType_ML: PropTypes.string
        })
    }).isRequired,
    onRatingPaymentMethodChange: PropTypes.func.isRequired
};

MLPaymentMethodComponent.defaultProps = {
    hasSsn: false,
    hasDepartmentNumber: false,
    hasEmployeeNumber: false,
    hasMetroBadgeNumber: false,
    hasMortgagees: false,
    isEndorsement: false,
    isCitrixSecureEnv: false
};

export default MLPaymentMethodComponent;
